import { useEffect, useState } from "react";
import type { CredorTipo } from "@atlas/sdk";
import { CredorTipoPill } from "../../lib/credor-tipo";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, FilterChips, Pagination, Pill, SelectField, TextField, type Column } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { contratoStatusInfo } from "../../lib/contrato-status";
import { produtoLabelDeContrato, deriveProdutoChave } from "@atlas/types";
import { TotalEmFolha, ValorSolicitado } from "../../components/ValorContrato";
import { InformarDadosLegadoModal } from "../../components/InformarDadosLegadoModal";
import { CronogramaLegadoModal } from "../../components/CronogramaLegadoModal";
import { HistoricoPagamentosContratoModal } from "../../components/HistoricoPagamentosContratoModal";
import type { DadosInformadosDoContrato } from "../../lib/dados-informados";
// `aplicarFiltros`/`contagens`/`recortar` sairam daqui na 007: os tres operavam
// sobre a lista inteira, que a tela nao tem mais. O equivalente roda na rota,
// com a mesma semantica `exceto` (FR-010).
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useDebouncedValue } from "../../lib/use-debounce";

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const DT_BR = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

type ContratoRow = {
  adf: string; situacao: string; lancamento: string; expiracao: string | null;
  cpfMasked: string; matricula: string; nome: string; tipoContrato: string;
  totalParcelas: number; valorParcela: number; convenio: string;
  convenioId: string; valorFinanciado: number; taxaAm: number;
  folhaStatus?: string;
  atualizadoEm?: string;
  bancoId: number; bancoNome: string;
  /** Parceiro, consignatária ou credor a identificar (ADR-0010). */
  credorTipo?: CredorTipo;
  /** `legado` = dívida que já estava na folha quando a prefeitura entrou, ou que
   *  foi aceita na conciliação. Não é categoria à parte: é contrato com origem
   *  diferente (ADR-0003). */
  origem?: "atlas" | "legado";
  /** No legado a Atlas não tem CCB e não conhece taxa, CET nem valor financiado.
   *  Os campos vêm zero, e a tela mostra `—`. */
  dadosFinanceirosConhecidos?: boolean;
  /** Feature 012: quem informou principal e taxa do legado, e quando. */
  dadosInformados?: DadosInformadosDoContrato | null;
  parcelasPagas?: number;
  cronogramaPendente?: boolean;
  numeroContratoOrigem?: string;
  migracaoLegado?: { motivoRetorno: string; pendenciaCadastro: boolean; bloqueioDesconto: boolean; quitacaoInformada: boolean };
};

// Rotulo do produto via helper unificado (lib/produto-label) — cobre
// Telemedicina/Portabilidade/Cartao Beneficio/Cartao Consignado etc.
// Antes: versao local so distinguia REFIN/ECONSIGNADO/Emprestimo.
// Pill de situacao via contratoStatusInfo (lib/contrato-status).

function folhaVariant(s?: string): "aceita" | "pendente" | "expirado" | "rejeitada" {
  if (!s) return "pendente";
  if (s === "aplicada") return "aceita";
  if (s === "falha") return "rejeitada";
  if (s === "recebida") return "pendente";
  return "expirado";
}

export function AdminContratos() {
  const [busca, setBusca] = useState("");
  const [bancoFiltro, setBancoFiltro] = useState<string>("");
  const [situacaoFiltro, setSituacaoFiltro] = useState<string>("");
  const [origemFiltro, setOrigemFiltro] = useState<"" | "atlas" | "legado">("");
  const [cronogramaFiltro, setCronogramaFiltro] = useState<"" | "pendente" | "informado">("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // O legado cujos dados a averbadora vai informar — a EXCEÇÃO da feature 012
  // (o caminho normal é o banco credor, no portal dele). Por isso o modal pede
  // motivo quando aberto daqui.
  const [informar, setInformar] = useState<ContratoRow | null>(null);
  const [cronograma, setCronograma] = useState<ContratoRow | null>(null);
  const [detalhes, setDetalhes] = useState<ContratoRow | null>(null);
  const qc = useQueryClient();
  // Sem debounce cada tecla e uma queryKey nova, ou seja um request.
  const buscaDebounced = useDebouncedValue(busca);
  // Trocar filtro com a posicao antiga cairia num offset que ja nao existe.
  useEffect(() => { setPagina(1); }, [buscaDebounced, bancoFiltro, situacaoFiltro, origemFiltro, cronogramaFiltro]);

  // Os quatro filtros sobem para a rota junto com o recorte. Subir o recorte
  // sozinho faria a busca procurar apenas dentro da pagina — o operador digita
  // um ADF que existe e a tela diz que nao existe, sem erro nenhum.
  const q = useQuery({
    queryKey: ["admin", "contratos", buscaDebounced, bancoFiltro, situacaoFiltro, origemFiltro, cronogramaFiltro, pagina, porPagina],
    queryFn: () => atlas.admin.contratos({
      q: buscaDebounced || undefined,
      banco: bancoFiltro || undefined,
      situacao: situacaoFiltro || undefined,
      origem: origemFiltro || undefined,
      cronograma: cronogramaFiltro || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    refetchInterval: 15_000,
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });

  const porOrigem = q.data?.porOrigem ?? [];

  // FACETAS, TOTAIS E OPCOES VEM DA API desde a 007, e nao ha alternativa: os
  // tres falam do RECORTE inteiro, e a tela so tem a pagina. Calcula-los sobre
  // `q.data.contratos` daria um KPI de 25 linhas e chips contando so a pagina —
  // numeros menores, plausiveis e errados (FR-014).
  const facetas = q.data?.facetas ?? { bancos: [], situacoes: [], origens: [] };
  const totais = q.data?.totais ?? { financiado: 0, parcelas: 0, terminaisIgnoradas: 0, semPrincipal: 0 };
  const contagemPor = (lista: { valor: string; contagem: number }[]) =>
    Object.fromEntries(lista.map((f) => [f.valor, f.contagem])) as Record<string, number>;
  const contagemOrigem = contagemPor(facetas.origens);
  const contagemBanco = contagemPor(facetas.bancos);
  const contagemSituacao = contagemPor(facetas.situacoes);
  const bancos = facetas.bancos.map((f) => f.valor);
  const situacoes = facetas.situacoes.map((f) => f.valor);

  const totalFinanciado = totais.financiado;
  const totalParcelas = totais.parcelas;
  const totalTerminaisIgnoradas = totais.terminaisIgnoradas;
  const semPrincipal = totais.semPrincipal;
  // O KPI e a coluna "Valor solicitado" respondem coisas DIFERENTES de
  // proposito: o KPI e' "quanto a ATLAS financiou" — o legado fica fora por
  // origem, mesmo depois de o credor informar o principal dele (feature 012),
  // porque a Atlas nao o emprestou. Somar a coluna na mao nao bate com o KPI, e
  // sem dizer isso a diferenca parece erro de conta. O hint fecha essa lacuna.
  const hintFinanciado = [
    totalTerminaisIgnoradas > 0 ? "só operações vivas" : null,
    semPrincipal > 0 ? `${semPrincipal} legado(s) fora — a Atlas não financiou` : null,
  ].filter(Boolean).join(" · ") || undefined;

  const algumFiltro = !!(busca || bancoFiltro || situacaoFiltro || origemFiltro || cronogramaFiltro);
  const limparFiltros = () => {
    setBusca(""); setBancoFiltro(""); setSituacaoFiltro(""); setOrigemFiltro(""); setCronogramaFiltro(""); setPagina(1);
  };

  // A pagina JA vem fatiada, filtrada e ordenada. `recorteDoServidor` da a MESMA
  // forma que o recorte no cliente, e e o que deixa a diferenca invisivel para
  // o `Pagination`.
  const daPagina = recorteDoServidor(q.data?.contratos ?? [], q.data?.total ?? 0, pagina, porPagina);


  const columns: Column<ContratoRow>[] = [
    { key: "adf", header: "ADF", mono: true, render: (r) => <><div>{r.adf}</div>{r.numeroContratoOrigem ? <div style={{ fontSize: 11 }}>Origem: {r.numeroContratoOrigem}</div> : null}</> },
    { key: "banco", header: "Credor", render: (r) => (
      <>
        <div>{r.bancoNome}</div>
        <CredorTipoPill tipo={r.credorTipo} />
      </>
    ) },
    { key: "servidor", header: "Servidor", render: (r) => (
      <>
        <div>{r.nome}</div>
        <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.cpfMasked} / {r.matricula}</div>
      </>
    ) },
    { key: "convenio", header: "Convênio", render: (r) => r.convenio },
    { key: "produto", header: "Produto", render: (r) => produtoLabelDeContrato(r) },
    { key: "origem", header: "Origem", render: (r) => (
      r.origem === "legado"
        ? (
          // `Pill` não aceita `title`; o span externo carrega o tooltip.
          <span title="Já estava na folha quando a organização entrou, ou foi aceita na conciliação">
            <Pill variant="pendente">legado</Pill>
          </span>
        )
        : <span style={{ color: "var(--text-dim)" }}>Atlas</span>
    ) },
    // DUAS colunas, dois nomes (feature 012, 11/09/2026). Aqui havia uma só,
    // "Valor", que mostrava o principal no contrato da Atlas e a soma das
    // parcelas no legado — total a pagar com juros ao lado de valor solicitado,
    // e o cliente concluiu que a comparação entre linhas perdia o sentido. O
    // legado sem informe mostra "—" com o selo; NUNCA R$ 0,00 (a tela de 29/08
    // que trocou o traço pela soma resolveu um sintoma criando outro).
    { key: "valorFinanciado", header: "Valor solicitado", align: "right", render: (r) => (
      <ValorSolicitado contrato={r} formatar={(n) => BRL.format(n)} />
    ) },
    { key: "parcelas", header: "Parcelas", align: "right", render: (r) => r.cronogramaPendente ? <><Pill variant="pendente">Cronograma pendente</Pill><div>{BRL.format(r.valorParcela)} por mês</div></> : <><div>{r.totalParcelas}× {BRL.format(r.valorParcela)}</div><div style={{ fontSize: 11 }}>{r.migracaoLegado?.quitacaoInformada ? `Quitação externa · ${r.parcelasPagas ?? 0} descontadas antes da baixa` : `${r.parcelasPagas ?? 0} descontadas · ${Math.max(0, r.totalParcelas - (r.parcelasPagas ?? 0))} restantes`}</div></> },
    { key: "totalEmFolha", header: "Total em folha", align: "right", render: (r) => (
      <TotalEmFolha contrato={r} formatar={(n) => BRL.format(n)} />
    ) },
    { key: "taxa", header: "Taxa a.m.", align: "right", render: (r) => {
      // Cartao consignado/beneficio nao tem taxa por parcela como emprestimo —
      // o custo real e a taxa da fatura, cobrada em atraso. Mostrar "0.00%"
      // ao lado dos 1.63% dos emprestimos enganava. Cliente 28/07/2026.
      const chave = deriveProdutoChave(r);
      if (chave === "CARTAO_CONSIGNADO" || chave === "CARTAO_BENEFICIO") {
        return <span style={{ color: "var(--text-dim)" }} title="Cartão não cobra juros por parcela — custo real é da fatura">—</span>;
      }
      // Mesmo raciocínio, outra causa: no legado a taxa é DESCONHECIDA, não
      // zero. "0.00% a.m." diria que o empréstimo é sem juros.
      if (r.dadosFinanceirosConhecidos === false) {
        return <span style={{ color: "var(--text-dim)" }} title="Contrato legado: a Atlas não tem a CCB e não conhece a taxa">—</span>;
      }
      return `${(r.taxaAm * 100).toFixed(2)}%`;
    } },
    { key: "situacao", header: "Status", render: (r) => {
      const info = contratoStatusInfo(r.situacao);
      return <><Pill variant={info.variant}>{info.label}</Pill>{r.migracaoLegado ? <div style={{ fontSize: 11, marginTop: 4 }}>{r.migracaoLegado.motivoRetorno || "Última crítica não informada"}{r.migracaoLegado.pendenciaCadastro ? <div>Cadastro da folha pendente</div> : null}</div> : null}</>;
    } },
    { key: "folha", header: "Folha", render: (r) => (
      <Pill variant={folhaVariant(r.folhaStatus)}>{r.folhaStatus ?? "—"}</Pill>
    ) },
    { key: "atualizadoEm", header: "Última atualização", render: (r) => (
      r.atualizadoEm ? DT_BR.format(new Date(r.atualizadoEm)) : "—"
    ) },
    // Só o legado tem ação aqui: informar (ou corrigir) o que a cédula diz. O
    // contrato da Atlas já nasce com ela. A averbadora é a exceção — o caminho
    // normal é o credor — e o modal cobra o motivo por isso.
    { key: "acoes", header: "", render: (r) => (
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <Button size="sm" variant="ghost" onClick={() => setDetalhes(r)}>
          Ver detalhes
        </Button>
        {r.origem === "legado" && !r.migracaoLegado?.quitacaoInformada ? (
        <Button size="sm" variant="ghost" onClick={() => setCronograma(r)}>{r.cronogramaPendente ? "Completar cronograma" : "Corrigir cronograma"}</Button>
        ) : null}
        {r.origem === "legado" && !r.cronogramaPendente ? (
        <Button size="sm" variant="ghost" onClick={() => setInformar(r)}>
          {r.dadosInformados ? "Corrigir dados" : "Informar dados"}
        </Button>
        ) : null}
      </div>
    ) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Contratos</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 780 }}>
          Visão consolidada de todos os contratos (todas as consignatárias, todas as prefeituras). Cada linha representa uma operação
          contratada — inclui propostas aprovadas aguardando ADF, contratos ativos averbados, quitados, cancelados e em
          cobrança direta.
        </p>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <SummaryCard
          label="Total de contratos"
          // O total do RECORTE, que vem da API — `daPagina.itens.length` seria
          // o tamanho da pagina e mostraria "25" em qualquer base.
          value={String(daPagina.total)}
          hint={totalTerminaisIgnoradas > 0 ? `${totalTerminaisIgnoradas} cancelado(s)/expirado(s)/quitado(s) — nao somados` : undefined}
        />
        <SummaryCard label="Valor financiado" value={BRL.format(totalFinanciado)} accent hint={hintFinanciado} />
        <SummaryCard label="Soma das parcelas" value={BRL.format(totalParcelas)} hint={totalTerminaisIgnoradas > 0 ? "so operacoes vivas" : undefined} />
      </div>

      {porOrigem.length > 0 && (
        <section>
          <h2 style={{ fontSize: "1.05rem", margin: "0 0 4px" }}>Atlas × legado, por folha</h2>
          <p style={{ color: "var(--text-muted)", margin: "0 0 12px", fontSize: 13, maxWidth: 780 }}>
            Soma das <strong>parcelas mensais da carteira</strong>, incluindo contratos suspensos.
            Consulte Folhas para conferir o valor efetivamente instruído ou confirmado em cada competência.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
            {porOrigem.map((r) => (
              <div
                key={r.unidadeId}
                style={{
                  background: "var(--bg-elev)",
                  border: "1px solid var(--border-strong)",
                  borderRadius: 12,
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 10 }}>{r.prefeituraNome}</div>
                <LinhaOrigem rotulo="Nasceu na Atlas" t={r.atlas} />
                <LinhaOrigem rotulo="Legado" t={r.legado} />
                <div style={{ borderTop: "1px solid var(--border-strong)", margin: "8px 0" }} />
                <LinhaOrigem rotulo="Total da carteira" t={r.total} forte />
                <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 6 }}>
                  {Math.round(r.fracaoLegado * 100)}% legado
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ORIGEM vira chip: é o corte que mais muda esta lista depois de uma carga
          inicial, quando o tombamento inunda a tabela, e merece um clique. As
          contagens saem da base com os OUTROS filtros aplicados, menos o da
          própria dimensão — número que ignora os demais filtros mente sobre o
          que acontece ao clicar, e o operador só descobre depois (FR-010). */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SelectField label="Cronograma" value={cronogramaFiltro} onChange={(e) => setCronogramaFiltro(e.target.value as "" | "pendente" | "informado")} options={[{ value: "", label: "Todos" }, { value: "pendente", label: "Cronograma pendente" }, { value: "informado", label: "Cronograma informado" }]} />
        <FilterChips
          label="Origem"
          value={origemFiltro}
          onChange={(v) => setOrigemFiltro(v as "" | "atlas" | "legado")}
          options={[
            // "Todas" e a soma das facetas de origem — ja calculadas com todos
            // os outros filtros aplicados menos o de origem (FR-010).
            { value: "", label: "Todas", count: facetas.origens.reduce((s, f) => s + f.contagem, 0) },
            { value: "atlas", label: "Nasceu na Atlas", count: contagemOrigem.atlas ?? 0 },
            { value: "legado", label: "Legado", cor: "var(--gold-500)", count: contagemOrigem.legado ?? 0 },
          ]}
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 200px 200px auto", gap: 12, alignItems: "end" }}>
          <TextField
            label="Buscar (ADF, contrato de origem, matrícula, CPF ou nome)"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Ex.: 9533469, 908983, ABSALAO…"
          />
          <SelectField
            label="Consignatária"
            value={bancoFiltro}
            onChange={(e) => setBancoFiltro(e.target.value)}
            options={[
              { value: "", label: "Todos" },
              ...bancos.map((b) => ({ value: b, label: `${b} (${contagemBanco[b] ?? 0})` })),
            ]}
          />
          <SelectField
            label="Situação"
            value={situacaoFiltro}
            onChange={(e) => setSituacaoFiltro(e.target.value)}
            options={[
              { value: "", label: "Todas" },
              ...situacoes.map((s) => ({ value: s, label: `${s} (${contagemSituacao[s] ?? 0})` })),
            ]}
          />
          {algumFiltro ? (
            <Button size="sm" variant="ghost" onClick={limparFiltros}>↻ Limpar filtros</Button>
          ) : null}
        </div>
      </div>

      {/* Recorte no CLIENTE, e não no servidor, de propósito. `/v1/admin/contratos`
          já aceita `limit`/`offset`, mas não aceita nenhum dos quatro filtros
          desta tela (busca, banco, situação, origem). Paginar no servidor faria
          a busca procurar só dentro da página — o operador digitaria um nome que
          existe e a tela diria que não existe, sem erro nenhum. A troca só é
          segura quando os filtros subirem para a rota. */}
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => r.adf}
        loading={q.isLoading}
        // Distinguir "nada bate com o filtro" de "nao ha contrato" (FR-013).
        // Antes isto saia de `rows.length`, a base inteira; com o recorte no
        // servidor a tela nao a tem mais, e quem responde e o proprio filtro.
        emptyState={
          algumFiltro
            ? "Nenhum contrato bate com os filtros aplicados."
            : "Nenhum contrato ainda — assim que uma consignatária criar uma proposta, ela aparece aqui."
        }
      />

      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="contratos"
      />

      {cronograma ? <CronogramaLegadoModal adf={cronograma.adf} nome={cronograma.nome} totalParcelas={cronograma.totalParcelas} parcelasPagas={cronograma.parcelasPagas ?? 0} papel="averbadora" onClose={() => setCronograma(null)} onSalvo={() => { qc.invalidateQueries({ queryKey: ["admin", "contratos"] }); }} /> : null}
      {informar ? (
        <InformarDadosLegadoModal
          adf={informar.adf}
          nome={informar.nome}
          valorParcela={informar.valorParcela}
          totalParcelas={informar.totalParcelas}
          atual={informar.dadosInformados ?? null}
          papel="averbadora"
          onClose={() => setInformar(null)}
          onSalvo={() => { qc.invalidateQueries({ queryKey: ["admin", "contratos"] }); }}
        />
      ) : null}
      {detalhes ? (
        <HistoricoPagamentosContratoModal
          adf={detalhes.adf}
          carregar={() => atlas.admin.historicoPagamentosContrato(detalhes.adf)}
          operacional
          onClose={() => setDetalhes(null)}
        />
      ) : null}
    </div>
  );
}

function LinhaOrigem({
  rotulo,
  t,
  forte,
}: {
  rotulo: string;
  t: { parcelaMensal: number; dividas: number };
  forte?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "2px 0" }}>
      <span style={{ color: forte ? "inherit" : "var(--text-muted)", fontWeight: forte ? 700 : 400 }}>{rotulo}</span>
      <span style={{ fontWeight: forte ? 700 : 400, whiteSpace: "nowrap" }}>
        {BRL.format(t.parcelaMensal)}/mês
        <span style={{ color: "var(--text-dim)", fontWeight: 400 }}> · {t.dividas} dívida(s)</span>
      </span>
    </div>
  );
}

function SummaryCard({ label, value, accent, hint }: { label: string; value: string; accent?: boolean; hint?: string }) {
  return (
    <div style={{
      background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
      borderRadius: 12, padding: "14px 16px",
    }}>
      <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6, color: accent ? "var(--gold-500)" : "var(--text)" }}>{value}</div>
      {hint ? <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}
